import React, { useState, useEffect, useRef, useContext } from 'react';
import { get } from '../../../utils/apiClient';
import { logger } from "../../../utils/logger";
import {
  getMedicationLifecycle,
  isOutsideMedication,
  medicationDisplayOrigin,
} from '../../../utils/medicationOrigin';
import MedicationSummaryRow, { MedMetaLine } from '../../../pages/clients/tabs/MedicationSummaryRow';
import MedicationSectionCard, {
  formatMedicationDate,
  insuranceFillDateLabel,
  medicationStartDateLabel,
} from '../../medications/MedicationSectionCard';
import {
  medicationNeedsInsuranceReconciliation,
  medicationStatusChip,
  pastMedicationStatusChip,
} from '../../../utils/medicationLabels';
import { transformMedicationsForForm } from '../../../features/notes/utils/chartDataHelpers';
import { DialogContext } from '../../../contexts/DialogContext';

/**
 * MedicationAutocomplete - Medication search with autocomplete
 * Allows practitioners to search and select medications by typing
 * Similar to DiagnosisAutocomplete but for medications
 * 
 * Props:
 * - isMedicationList: boolean - When true, renders as a full medication list (used for medication_list field type)
 * - isSupplementList: boolean - When true, customizes labels/placeholders for supplements instead of medications
 * - outsideSectionMode: "standalone" or "grouped" - Controls the outside-entry section presentation
 */
const isDoseSpotManaged = (medication) =>
  Boolean(
    medication?.vendor_prescription_id
      && medication?.prescribing_responsibility === 'prescribed_by_our_practice',
  );

const isPracticeManaged = (medication) => (
  medication?.source === 'prescribed_in_house'
  || medication?.prescribing_responsibility === 'prescribed_by_our_practice'
);

const MedicationAutocomplete = ({
  field,
  value,
  onChange,
  error,
  readOnly,
  isMedicationList = false,
  isSupplementList = false,
  outsideSectionMode = 'standalone',
  patientId,
  canPrescribe = false,
}) => {
  void isMedicationList;
  // useContext (not useDialog, which throws): unit tests render this
  // component bare, outside a DialogProvider. The real app always
  // provides one; when absent the refresh proceeds without the
  // destructive-action confirmation.
  const dialog = useContext(DialogContext);
  const confirm = dialog ? dialog.confirm : async () => true;
  const isGroupedOutsideSection = outsideSectionMode === 'grouped';
  // Determine labels based on whether this is for supplements or medications
  const itemLabel = isSupplementList ? 'supplement' : 'medication';
  const itemLabelPlural = isSupplementList ? 'supplements' : 'medications';
  const practiceLabel = isSupplementList ? 'Recommended by Our Practice' : 'Prescribed by Our Practice';
  const outsideLabel = isSupplementList ? 'Client-Reported Supplements' : 'Outside Medications';
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showOutsideEntry, setShowOutsideEntry] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualMedication, setManualMedication] = useState('');
  const [expandedMedicationIndex, setExpandedMedicationIndex] = useState(null);
  const [pastHistoryExpanded, setPastHistoryExpanded] = useState(false);
  // Ensure value is always an array
  const normalizeValue = (val) => {
    if (Array.isArray(val)) return val;
    if (val === null || val === undefined || val === '') return [];
    // If it's a string (legacy free text), convert to array with one item
    if (typeof val === 'string') {
      // Try to parse as newline-separated list
      const items = val.split('\n').filter(item => item.trim());
      return items.map(item => ({
        id: null,
        name: item.trim(),
        isManual: true
      }));
    }
    if (typeof val === 'object') return [val];
    return [];
  };
  
  const [selectedMedications, setSelectedMedications] = useState(normalizeValue(value));
  const [medicationsChanged, setMedicationsChanged] = useState(false);
  const [refreshingMeds, setRefreshingMeds] = useState(false);
  const medBaselineRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // Non-destructive "medications changed" signal: the note's prefilled list
  // can go stale when the chart changes elsewhere (e.g. a DoseSpot sync).
  // Compared against the signature captured at mount — the clinician's own
  // in-note edits never count as a chart change, and refresh is explicit.
  // Every persisted/editable field refresh replaces — a change confined to
  // any one of these must count both as a chart change (fresh != baseline)
  // and as an in-note edit (current != baseline).
  const MED_SIGNATURE_FIELDS = [
    'id', 'name', 'medication_name', 'status', 'clinical_status',
    'dosage', 'frequency', 'route', 'start_date', 'end_date',
    'prescriber_name', 'external_prescriber_name', 'source',
    'prescribing_responsibility', 'item_type', 'source_reference',
    'vendor_prescription_id', 'is_in_house', 'managed_by',
    'record_provenance', 'rx_status', 'insurance_history',
  ];
  const medSignature = (items) => JSON.stringify(
    (items || [])
      .map((m) => MED_SIGNATURE_FIELDS.map((f) => m[f] ?? null))
      .sort(),
  );
  const itemType = isSupplementList ? 'supplement' : 'medication';
  const fetchFreshMeds = async () => {
    const resp = await get(`/api/medications/patients/${patientId}/medications`);
    if (!resp.ok) return null;
    const data = await resp.json();
    const filtered = (data.medications || []).filter(
      (m) => (m.item_type || 'medication') === itemType,
    );
    return transformMedicationsForForm(filtered, {
      includeOutside: true,
      includeHistory: true,
    });
  };

  useEffect(() => {
    if (!patientId || readOnly) return undefined;
    if (medBaselineRef.current === null) {
      medBaselineRef.current = medSignature(normalizeValue(value));
    }
    let cancelled = false;
    const check = async () => {
      try {
        const fresh = await fetchFreshMeds();
        if (cancelled || fresh === null) return;
        setMedicationsChanged(medSignature(fresh) !== medBaselineRef.current);
      } catch {
        // Transient fetch failures leave the badge untouched.
      }
    };
    check();
    window.addEventListener('focus', check);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', check);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, readOnly]);

  const handleMedicationsRefresh = async () => {
    // Protect unsaved in-note medication edits: replacing the list would
    // discard them, so confirm first.
    if (
      medSignature(normalizeValue(value)) !== medBaselineRef.current
      && !(await confirm({
        title: 'Refresh medications',
        message:
          'Refreshing replaces the medication list shown here with the current chart list. Your in-note medication edits will be replaced.',
        confirmText: 'Refresh',
        variant: 'danger',
      }))
    ) {
      return;
    }
    setRefreshingMeds(true);
    try {
      const fresh = await fetchFreshMeds();
      if (fresh !== null) {
        medBaselineRef.current = medSignature(fresh);
        onChange(fresh);
        setMedicationsChanged(false);
      }
    } finally {
      setRefreshingMeds(false);
    }
  };

  useEffect(() => {
    setSelectedMedications(normalizeValue(value));
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchMedications = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Pass item_type to filter by medication or supplement
      const itemTypeParam = isSupplementList ? 'supplement' : 'medication';
      const response = await get(
        `/api/medications/search?query=${encodeURIComponent(query)}&limit=20&item_type=${itemTypeParam}`
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.medications || []);
        setShowDropdown(true);
      } else {
        logger.error('Failed to search medications');
        setSearchResults([]);
      }
    } catch (error) {
      logger.error('Error searching medications:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchMedications(query);
    }, 300);
  };

  const handleSelectMedication = (medication) => {
    const isAlreadySelected = selectedMedications.some(
      m => m.id === medication.id || (m.name && m.name.toLowerCase() === medication.generic_name.toLowerCase())
    );

    if (isAlreadySelected) {
      return;
    }

    const newMedication = {
      id: medication.id,
      name: medication.generic_name,
      brand_names: medication.brand_names,
      drug_class: medication.drug_class,
      controlled_substance_schedule: medication.controlled_substance_schedule,
      dosage: '',
      frequency: '',
      notes: '',
      prescriber_name: '',
      source: 'self_reported',
      status: 'active',
      clinical_status: 'current',
      isManual: false
    };

    const updatedMedications = [...selectedMedications, newMedication];
    setSelectedMedications(updatedMedications);
    onChange(updatedMedications);
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
  };

  const handleAddManualMedication = () => {
    if (!manualMedication.trim()) return;
    
    const isAlreadySelected = selectedMedications.some(
      m => m.name && m.name.toLowerCase() === manualMedication.trim().toLowerCase()
    );

    if (isAlreadySelected) {
      setManualMedication('');
      setShowManualEntry(false);
      return;
    }

    const newMedication = {
      id: null,
      name: manualMedication.trim(),
      dosage: '',
      frequency: '',
      notes: '',
      prescriber_name: '',
      source: 'self_reported',
      status: 'active',
      clinical_status: 'current',
      isManual: true
    };

    const updatedMedications = [...selectedMedications, newMedication];
    setSelectedMedications(updatedMedications);
    onChange(updatedMedications);
    setManualMedication('');
    setShowManualEntry(false);
  };

  const handleRemoveMedication = (index) => {
    const updatedMedications = selectedMedications.filter((_, i) => i !== index);
    setSelectedMedications(updatedMedications);
    onChange(updatedMedications);
  };

  const handleMedicationDetailChange = (index, field, value) => {
    const updatedMedications = selectedMedications.map((med, i) => 
      i === index ? { ...med, [field]: value } : med
    );
    setSelectedMedications(updatedMedications);
    onChange(updatedMedications);
  };

  const groups = { practice: [], outside: [], insurance: [], unknown: [], past: [] };
  selectedMedications.forEach((medication, index) => {
    const entry = { medication, index };
    const lifecycle = getMedicationLifecycle(medication);
    if (medicationNeedsInsuranceReconciliation(medication)) groups.insurance.push(entry);
    else if (lifecycle === 'past') groups.past.push(entry);
    else if (lifecycle === 'unknown') groups.unknown.push(entry);
    else if (isPracticeManaged(medication) || !isOutsideMedication(medication)) groups.practice.push(entry);
    else groups.outside.push(entry);
  });

  const renderMedicationRow = ({ medication, index }, { statusLabel, statusTone, badge = undefined, dateLabel }) => (
    <MedicationSummaryRow
      key={`compact-${medication.id || medication.name}-${index}`}
      medication={{
        medication_name: medication.name || medication.medication_name,
        dosage: medication.dosage,
        frequency: medication.frequency,
      }}
      statusLabel={statusLabel}
      statusTone={statusTone}
      badge={badge}
      dateLabel={dateLabel}
      expanded={expandedMedicationIndex === index}
      onToggle={!readOnly ? () => setExpandedMedicationIndex(
        expandedMedicationIndex === index ? null : index,
      ) : undefined}
    >
              <div className="space-y-2 text-sm text-gray-600">
                {medicationDisplayOrigin(medication) && (
                  <p>{medicationDisplayOrigin(medication)}</p>
                )}
                {(medication.managed_by != null || medication.record_provenance != null) && (
                  <MedMetaLine med={medication} />
                )}
                {medication.prescriber_name && (
                  <p>
                    {isOutsideMedication(medication) ? 'Outside prescriber name' : 'Prescriber'}: {medication.prescriber_name}
                  </p>
                )}
                {!readOnly && (!isPracticeManaged(medication) || canPrescribe) && !isDoseSpotManaged(medication) && (
                  <>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <input
                        type="text"
                        value={medication.dosage || ''}
                        onChange={(event) => handleMedicationDetailChange(index, 'dosage', event.target.value)}
                        placeholder="Dosage"
                        className="min-w-0 rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                      <input
                        type="text"
                        value={medication.frequency || ''}
                        onChange={(event) => handleMedicationDetailChange(index, 'frequency', event.target.value)}
                        placeholder="Frequency"
                        className="min-w-0 rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                      <input
                        type="text"
                        value={medication.notes || ''}
                        onChange={(event) => handleMedicationDetailChange(index, 'notes', event.target.value)}
                        placeholder="Notes"
                        className="min-w-0 rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                    </div>
                    <label className="block text-sm">
                      <span className="mb-1 block">Outside prescriber name</span>
                      <input
                        type="text"
                        value={medication.prescriber_name || ''}
                        onChange={(event) => handleMedicationDetailChange(index, 'prescriber_name', event.target.value)}
                        placeholder="Unknown or enter name"
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                    </label>
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => handleRemoveMedication(index)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove {itemLabel}
                      </button>
                      <button
                        type="button"
                        onClick={() => setExpandedMedicationIndex(null)}
                        className="text-sm text-green-700 hover:text-green-900"
                      >
                        Done
                      </button>
                    </div>
                  </>
                )}
                {!readOnly && isDoseSpotManaged(medication) && (
                  <p className="text-sm text-indigo-700">Manage this prescription in e-Prescribe.</p>
                )}
                {!readOnly && isPracticeManaged(medication) && !canPrescribe && (
                  <p className="text-sm text-indigo-700">
                    Only prescribers can edit medications managed by this practice.
                  </p>
                )}
              </div>
    </MedicationSummaryRow>
  );

  const toChipProps = (chip) => ({ statusLabel: chip.label, statusTone: chip.tone });
  const currentRowProps = (medication) => ({
    ...toChipProps(medicationStatusChip(medication)),
    dateLabel: medicationStartDateLabel(medication, 'Prescribed')
      ?? medicationStartDateLabel(medication, 'Started') ?? undefined,
  });
  const unknownRowProps = (medication) => ({
    ...toChipProps(medicationStatusChip(medication)),
    dateLabel: medicationStartDateLabel(medication, 'Recorded') ?? undefined,
  });
  const pastRowProps = (medication) => ({
    ...toChipProps(pastMedicationStatusChip(medication)),
    dateLabel: medication.end_date
      ? `Ended ${formatMedicationDate(medication.end_date)}`
      : undefined,
  });
  const insuranceRowProps = (medication) => ({
    statusLabel: 'Needs reconciliation',
    statusTone: 'warning',
    badge: 'Insurance',
    dateLabel: insuranceFillDateLabel(medication) ?? undefined,
  });

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {field.help_text && (
        <p className="text-sm text-gray-500 mb-2">{field.help_text}</p>
      )}

      {medicationsChanged && !readOnly && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          <span>Medications changed — refresh available</span>
          <button
            type="button"
            onClick={handleMedicationsRefresh}
            disabled={refreshingMeds}
            className="font-medium text-amber-900 underline hover:text-amber-700 disabled:opacity-50 dark:text-amber-200"
          >
            {refreshingMeds ? 'Refreshing…' : 'Refresh medications'}
          </button>
        </div>
      )}

      {/* Selected Medications — same tinted sections as the chart tab */}
      {selectedMedications.length > 0 && (
        <div className="mb-3 space-y-3">
          {groups.practice.length > 0 && (
            <MedicationSectionCard tone="practice" title={`Current — ${practiceLabel.toLowerCase()}`} count={groups.practice.length}>
              <div className="space-y-1">
                {groups.practice.map((entry) => renderMedicationRow(entry, currentRowProps(entry.medication)))}
              </div>
            </MedicationSectionCard>
          )}
          {groups.outside.length > 0 && (
            <MedicationSectionCard tone="outside" title={outsideLabel} count={groups.outside.length}>
              <div className="space-y-1">
                {groups.outside.map((entry) => renderMedicationRow(entry, currentRowProps(entry.medication)))}
              </div>
            </MedicationSectionCard>
          )}
          {groups.insurance.length > 0 && (
            <MedicationSectionCard
              tone="insurance"
              title="Insurance-reported history — needs reconciliation"
              subtitle="Reported by the payer's fill history. Confirm whether each medication belongs on the current list."
              count={groups.insurance.length}
            >
              <div className="space-y-1">
                {groups.insurance.map((entry) => renderMedicationRow(entry, insuranceRowProps(entry.medication)))}
              </div>
            </MedicationSectionCard>
          )}
          {groups.unknown.length > 0 && (
            <MedicationSectionCard tone="unknown" title="Current status unknown" count={groups.unknown.length}>
              <div className="space-y-1">
                {groups.unknown.map((entry) => renderMedicationRow(entry, unknownRowProps(entry.medication)))}
              </div>
            </MedicationSectionCard>
          )}
          {groups.past.length > 0 && (
            <button
              type="button"
              onClick={() => setPastHistoryExpanded(value => !value)}
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
              aria-expanded={pastHistoryExpanded}
            >
              {pastHistoryExpanded ? 'Hide past history' : 'Show past history'}
            </button>
          )}
          {pastHistoryExpanded && groups.past.length > 0 && (
            <MedicationSectionCard tone="past" title="History" count={groups.past.length}>
              <div className="space-y-1">
                {groups.past.map((entry) => renderMedicationRow(entry, pastRowProps(entry.medication)))}
              </div>
            </MedicationSectionCard>
          )}
        </div>
      )}

      {/* Search Input */}
      {!readOnly && (
        <section
          aria-labelledby={`outside-${itemLabel}-heading`}
          className={isGroupedOutsideSection
            ? 'mt-3 border-t border-amber-200 pt-3'
            : 'mt-3 border-t border-amber-200 pt-3'}
        >
          <button
            type="button"
            onClick={() => setShowOutsideEntry(prev => !prev)}
            className="flex min-h-9 items-center gap-2 text-sm font-medium text-green-700 hover:text-green-800"
            aria-expanded={showOutsideEntry}
          >
            <span className="text-lg leading-none" aria-hidden="true">+</span>
            <span>
              {isSupplementList ? 'Add outside supplement' : 'Add outside medication'}
            </span>
          </button>
          {showOutsideEntry && <div className="mt-2">
          <div className="relative" ref={dropdownRef}>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchQuery.length >= 2 && setShowDropdown(true)}
              placeholder={`Search outside ${itemLabelPlural}...`}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          
          {isSearching && (
            <div className="absolute right-3 top-2.5">
              <div className="animate-spin h-5 w-5 border-2 border-green-500 border-t-transparent rounded-full"></div>
            </div>
          )}

          {/* Dropdown Results */}
          {showDropdown && searchResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((result) => (
                <button
                  key={result.id}
                  type="button"
                  onClick={() => handleSelectMedication(result)}
                  className="w-full text-left px-3 py-2 hover:bg-green-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-green-700">
                      {result.generic_name}
                    </span>
                    {result.controlled_substance_schedule && (
                      <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                        C-{result.controlled_substance_schedule}
                      </span>
                    )}
                  </div>
                  {result.brand_names && result.brand_names.length > 0 && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      Brand: {result.brand_names.slice(0, 3).join(', ')}
                      {result.brand_names.length > 3 && '...'}
                    </div>
                  )}
                  {result.drug_class && (
                    <div className="text-xs text-gray-500">
                      Class: {result.drug_class}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {showDropdown && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg p-3 text-sm text-gray-500">
              No {itemLabelPlural} found matching &quot;{searchQuery}&quot;
            </div>
          )}
          </div>
          {/* Manual Entry Option */}
          <div className="mt-2">
          {!showManualEntry ? (
            <button
              type="button"
              onClick={() => setShowManualEntry(true)}
              className="text-sm text-green-600 hover:text-green-800 underline"
            >
              + Document outside {itemLabel} manually
            </button>
          ) : (
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={manualMedication}
                onChange={(e) => setManualMedication(e.target.value)}
                placeholder={`Enter outside ${itemLabel} name...`}
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddManualMedication();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddManualMedication}
                className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowManualEntry(false);
                  setManualMedication('');
                }}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          )}
          </div>
          </div>}
        </section>
      )}

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default MedicationAutocomplete;
